// Circuit-trace effect engine: traces grow in 45° steps from a press point,
// branch while held, and on release fade from tail (origin) to tip.
const STEP = 7;
const DIRS = [0, 1, 2, 3, 4, 5, 6, 7].map((i) => [Math.cos((i * Math.PI) / 4), Math.sin((i * Math.PI) / 4)]);
const MAX_TRACES = 420;

const MAX_HOLD = 20000;

export function createCircuitEngine(canvas, color, onFinish) {
  const ctx = canvas.getContext('2d');
  let traces = [];
  let press = null;
  let pending = null;

  const release = () => {
    if (!press) return;
    pending = { burst: press.burst, x: press.x, y: press.y, held: performance.now() - press.start };
    press = null;
  };
  let raf = null;
  let dpr = 1;

  const resize = () => {
    dpr = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = innerWidth * dpr;
    canvas.height = innerHeight * dpr;
  };

  const spawn = (x, y, dir, burst, depth = 0) => {
    if (traces.length >= MAX_TRACES) return;
    traces.push({ pts: [{ x, y }], nodes: [], dir, burst, depth, growing: true, fade: 0,
      life: 40 + Math.random() * 90 - depth * 12, speed: 1 + Math.random() * 1.5 });
  };

  const burstAt = (burst, x, y, n) => {
    for (let i = 0; i < n; i++) spawn(x, y, Math.floor(Math.random() * 8), burst);
  };

  const grow = (t) => {
    for (let s = 0; s < t.speed; s++) {
      const h = t.pts[t.pts.length - 1];
      if (Math.random() < 0.08) { t.dir = (t.dir + (Math.random() < 0.5 ? 1 : 7)) % 8; t.nodes.push(t.pts.length - 1); }
      const [dx, dy] = DIRS[t.dir];
      const p = { x: h.x + dx * STEP, y: h.y + dy * STEP };
      t.pts.push(p);
      t.life--;
      const held = press && press.burst === t.burst;
      if (held && t.depth < 5 && Math.random() < 0.025) spawn(p.x, p.y, (t.dir + (Math.random() < 0.5 ? 1 : 7)) % 8, t.burst, t.depth + 1);
      const out = p.x < -20 || p.y < -20 || p.x > innerWidth + 20 || p.y > innerHeight + 20;
      if (out || (!held && t.life <= 0)) { t.growing = false; t.nodes.push(t.pts.length - 1); return; }
    }
  };

  const draw = (t) => {
    const start = Math.floor(t.fade);
    const pts = t.pts;
    if (pts.length - start < 2) return;
    ctx.beginPath();
    ctx.moveTo(pts[start].x, pts[start].y);
    for (let i = start + 1; i < pts.length; i++) ctx.lineTo(pts[i].x, pts[i].y);
    const a = Math.max(0.25, 1 - t.depth * 0.12);
    ctx.strokeStyle = color(0.12 * a); ctx.lineWidth = 6; ctx.stroke();
    ctx.strokeStyle = color(0.85 * a); ctx.lineWidth = 1.4; ctx.stroke();
    for (const ni of t.nodes) if (ni >= start) {
      ctx.beginPath(); ctx.arc(pts[ni].x, pts[ni].y, 2.4, 0, 6.283);
      ctx.fillStyle = color(0.9 * a); ctx.fill();
    }
    const h = pts[pts.length - 1];
    if (t.growing) {
      ctx.beginPath(); ctx.arc(h.x, h.y, 5, 0, 6.283); ctx.fillStyle = color(0.25); ctx.fill();
      ctx.beginPath(); ctx.arc(h.x, h.y, 1.8, 0, 6.283); ctx.fillStyle = 'rgba(255,255,255,0.95)'; ctx.fill();
    }
    const tail = pts[start];
    if (t.fade > 0) { ctx.beginPath(); ctx.arc(tail.x, tail.y, 2, 0, 6.283); ctx.fillStyle = color(0.5); ctx.fill(); }
  };

  const frame = () => {
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, innerWidth, innerHeight);
    ctx.globalCompositeOperation = 'lighter';
    if (press && performance.now() - press.start >= MAX_HOLD) release();
    if (press && performance.now() - press.last > 220) { press.last = performance.now(); burstAt(press.burst, press.x, press.y, 3); }
    traces = traces.filter((t) => {
      if (t.growing) grow(t);
      const released = !press || press.burst !== t.burst;
      if (released) t.fade += 1.6 + t.fade * 0.015;
      draw(t);
      return t.fade < t.pts.length - 1;
    });
    if (pending && !traces.some((t) => t.burst === pending.burst)) { onFinish?.(pending); pending = null; }
    raf = traces.length || press || pending ? requestAnimationFrame(frame) : null;
  };

  const kick = () => { if (!raf) raf = requestAnimationFrame(frame); };

  return {
    resize,
    down(x, y) { release(); const burst = Symbol(); const now = performance.now(); press = { burst, x, y, start: now, last: now }; burstAt(burst, x, y, 7); kick(); },
    move(x, y) { if (press) { press.x = x; press.y = y; } },
    up: release,
    destroy() { if (raf) cancelAnimationFrame(raf); },
  };
}